import { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Row, Col, Button, InputGroup } from 'react-bootstrap';
import { getEmployees } from '../../../services/firebase/employeeService';
import { getMonthlyAttendance } from '../../../services/firebase/attendanceService';
import { useAuth } from '../../../context/AuthContext';
import { FaUsers, FaCheck, FaSearch, FaChevronDown, FaTimes } from 'react-icons/fa';
import { isDeptVisibleToUser } from '../../../utils/departmentVisibility';

const normalizeId = (id) => {
  if (id === null || id === undefined) return '';
  const s = String(id).trim();
  if (/^\d+$/.test(s)) return String(parseInt(s, 10));
  return s.toLowerCase();
};

const EmployeeSelector = ({
  selectedEmployeeIds = [],
  onEmployeeChange,
  onEmployeeSelect,
  disabled,
  month,
  year,
  isMultiSelect = true
}) => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const containerRef = useRef(null);

  const activeIds = useMemo(() => {
    return (Array.isArray(selectedEmployeeIds)
      ? selectedEmployeeIds
      : (selectedEmployeeIds ? [selectedEmployeeIds] : [])).map(id => String(id));
  }, [selectedEmployeeIds]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchEmployees();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, month, year]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const allRes = await getEmployees({ includeInactive: true });
      const allSystemEmployees = allRes?.data || [];

      // Create a map from normalized employee ID to their actual department from profile
      const empIdToDeptMap = new Map();
      const inactiveEmpIds = new Set();
      
      allSystemEmployees.forEach(emp => {
        const id1 = normalizeId(emp.employeeId);
        const id2 = normalizeId(emp.id || emp.uid);
        if (id1) empIdToDeptMap.set(id1, emp.department);
        if (id2) empIdToDeptMap.set(id2, emp.department);

        if (emp.isActive === false || emp.status === 'Inactive') {
          if (id1) inactiveEmpIds.add(id1);
          if (id2) inactiveEmpIds.add(id2);
        }
      });

      // Filter only active employees
      const activeSystemEmployees = allSystemEmployees.filter(emp => emp.isActive !== false && emp.status !== 'Inactive');

      // Filter by department if the logged-in user is a dept_head or production_head
      const isDeptOrProdHead = user?.role === 'dept_head' || user?.role === 'production_head';
      let rawEmployees = [...activeSystemEmployees];
      if (isDeptOrProdHead) {
        rawEmployees = rawEmployees.filter(emp => 
          isDeptVisibleToUser(emp.department, emp.department, user)
        );
      }

      const systemEmployees = rawEmployees.map(emp => ({
        uid: String(emp.id || emp.uid || ''),
        employeeId: String(emp.employeeId || emp.id || ''),
        fullName: `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.displayName || 'Unknown',
        source: 'system'
      }));

      const targetMonth = month || new Date().getMonth() + 1;
      const targetYear = year || new Date().getFullYear();

      let attendanceEmployees = [];
      try {
        const attResponse = await getMonthlyAttendance(targetMonth, targetYear);
        const records = attResponse?.data || [];
        const uniqueEmpMap = new Map();
        records.forEach(record => {
          const id = String(record.employeeId || '');
          const normId = normalizeId(id);
          
          // Skip inactive employees completely
          if (inactiveEmpIds.has(normId)) return;

          if (id && !uniqueEmpMap.has(id)) {
            if (isDeptOrProdHead) {
              // Use actual department from profile first, fall back to record's department
              const actualDept = empIdToDeptMap.get(normId) || record.department || record.dept || '';
              if (!isDeptVisibleToUser(actualDept, actualDept, user)) {
                return;
              }
            }
            uniqueEmpMap.set(id, { uid: id, employeeId: id, fullName: record.employeeName || 'Unknown', source: 'attendance' });
          }
        });
        attendanceEmployees = Array.from(uniqueEmpMap.values());
      } catch (err) { }

      const mergedEmployees = [...systemEmployees];
      const systemEmpIds = new Set();
      systemEmployees.forEach(e => {
        if (e.employeeId) systemEmpIds.add(normalizeId(e.employeeId));
        if (e.uid) systemEmpIds.add(normalizeId(e.uid));
      });
      attendanceEmployees.forEach(attEmp => {
        const normAttId = normalizeId(attEmp.employeeId || attEmp.uid);
        if (!inactiveEmpIds.has(normAttId) && !systemEmpIds.has(normAttId)) {
          mergedEmployees.push(attEmp);
        }
      });

      mergedEmployees.sort((a, b) => {
        const idA = String(a.employeeId || '').toLowerCase();
        const idB = String(b.employeeId || '').toLowerCase();
        return idA.localeCompare(idB, undefined, { numeric: true, sensitivity: 'base' });
      });

      setEmployees(mergedEmployees);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = useMemo(() => {
    if (!searchTerm) return employees;
    const search = searchTerm.toLowerCase();
    return employees.filter(emp =>
      emp.fullName?.toLowerCase().includes(search) ||
      emp.employeeId?.toString().toLowerCase().includes(search)
    );
  }, [employees, searchTerm]);

  const toggleEmployee = (id, empObj = null) => {
    const stringId = String(id);
    const targetEmp = empObj || employees.find(e => String(e.employeeId || e.uid) === stringId);
    if (!isMultiSelect) {
      onEmployeeChange(stringId);
      if (onEmployeeSelect && targetEmp) {
        onEmployeeSelect(targetEmp);
      }
      setIsMenuOpen(false);
      return;
    }

    const newSelection = activeIds.includes(stringId)
      ? activeIds.filter(i => i !== stringId)
      : [...activeIds, stringId];
    onEmployeeChange(newSelection);
    if (onEmployeeSelect && targetEmp) {
      onEmployeeSelect(targetEmp);
    }
  };

  const handleSelectAll = () => {
    if (!isMultiSelect) return;
    if (activeIds.length === filteredEmployees.length && activeIds.length > 0) {
      onEmployeeChange([]);
    } else {
      const allFilteredIds = filteredEmployees.map(e => String(e.employeeId || e.uid));
      onEmployeeChange([...new Set([...activeIds, ...allFilteredIds])]);
    }
  };

  const getToggleText = () => {
    if (activeIds.length === 0) return 'Select Employees';
    if (!isMultiSelect) {
      const emp = employees.find(e => {
        const id = String(e.employeeId || e.uid);
        return id === activeIds[0] || normalizeId(id) === normalizeId(activeIds[0]);
      });
      return emp ? emp.fullName : activeIds[0];
    }
    return `${activeIds.length} Staff Selected`;
  };

  return (
    <div className="w-100" ref={containerRef} style={{ position: 'relative', maxWidth: '600px' }}>
      <Button
        variant="light"
        className="w-100 border-0 bg-light fw-bold py-2 px-3 d-flex align-items-center justify-content-between shadow-sm transition-all"
        style={{ borderRadius: '12px', height: '48px', color: '#075555' }}
        disabled={disabled || loading}
        onClick={() => setIsMenuOpen(!isMenuOpen)}
      >
        <div className="d-flex align-items-center gap-2 text-truncate">
          <FaUsers className="text-primary" />
          <span className="text-truncate">{getToggleText()}</span>
        </div>
        <FaChevronDown className={`ms-2 small transition-all ${isMenuOpen ? 'rotate-180' : ''}`} style={{ transition: 'transform 0.2s' }} />
      </Button>

      {isMenuOpen && (
        <div
          className="shadow-lg border-0 bg-white"
          style={{
            position: 'absolute',
            top: '55px',
            left: 0,
            width: '100%',
            minWidth: '260px',
            maxWidth: '100%',
            zIndex: 1100,
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.1)',
            boxShadow: '0 15px 45px rgba(0,0,0,0.15)'
          }}
        >
          {/* Search Header */}
          <div className="p-3 border-bottom bg-white sticky-top" style={{ zIndex: 11 }}>
            <InputGroup className="bg-light rounded-pill px-2" style={{ border: 'none' }}>
              <InputGroup.Text className="bg-transparent border-0 text-muted">
                <FaSearch size={14} />
              </InputGroup.Text>
              <Form.Control
                autoFocus
                placeholder="Search staff..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-transparent border-0 shadow-none py-2"
                style={{ fontSize: '0.9rem' }}
              />
              {searchTerm && (
                <Button
                  variant="transparent"
                  className="border-0 text-muted p-0 pe-2"
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes size={12} />
                </Button>
              )}
            </InputGroup>

            <div className="d-flex justify-content-between align-items-center mt-2 px-1">
              {isMultiSelect ? (
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-decoration-none fw-bold text-primary"
                  onClick={handleSelectAll}
                >
                  {activeIds.length === filteredEmployees.length ? 'Unselect All' : 'Select All'}
                </Button>
              ) : <div />}
              <small className="text-muted fw-bold" style={{ fontSize: '0.75rem' }}>
                {filteredEmployees.length} staff found
              </small>
            </div>
          </div>

          {/* List Content */}
          <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((emp, idx) => {
                const empId = emp.employeeId || emp.uid || `emp-${idx}`;
                const isSelected = activeIds.some(id => id === String(empId) || normalizeId(id) === normalizeId(empId));
                return (
                  <div
                    key={empId}
                    onClick={() => toggleEmployee(empId, emp)}
                    className={`d-flex align-items-center justify-content-between px-3 py-2 border-bottom ${isSelected ? 'bg-primary-subtle bg-opacity-10' : ''}`}
                    style={{ cursor: 'pointer', minHeight: '50px' }}
                  >
                    <div className="d-flex align-items-center gap-3 overflow-hidden w-100">
                      <div className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${isSelected ? 'bg-primary text-white' : 'bg-light text-secondary'}`} style={{ width: '22px', height: '22px', border: isSelected ? 'none' : '1.5px solid #dee2e6' }}>
                        {isSelected && <FaCheck size={10} />}
                      </div>
                      <div className="d-flex flex-column overflow-hidden text-truncate">
                        <span className={`text-dark text-truncate ${isSelected ? 'fw-bold' : ''}`} style={{ fontSize: '0.9rem' }}>
                          {emp.fullName || 'Unnamed Employee'}
                        </span>
                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>ID: {empId}</small>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-muted small bg-light">
                <FaSearch className="mb-2 opacity-25" size={20} />
                <div>No staff matches "{searchTerm}"</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeSelector;
